import React, { useEffect } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getInitialRange } from '../../../../.globals/helpers';

// Components
import { Loading } from "@gpstrackit/gtc-react-controls";
import { Timezone } from 'Components/uiControls';
import { Divider } from '@mui/material';

// Actions
import { getCase } from './redux/slice'

export const CaseInfo = (props) => {
    const { caseNumber, openTooltip } = props
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { caseInfo, loading } = useSelector((state) => state.driverPerformanceEventsTabCaseInfoRedux);
    const user = useSelector(state => state.securityRedux.user);
    const { dateformat, timeZone } = user;
    const driverTabDateStart = useSelector((state) => state?.dashboardDriverPerformanceSliceRedux?.filters?.dateStart);
    const driverTabDateEnd = useSelector((state) => state?.dashboardDriverPerformanceSliceRedux?.filters?.dateEnd);
    const casesLastSearchRange = useSelector((state) => state?.driverPerformanceCasesTabRedux?.lastSearchRange);
    const dashboardGlobals = useSelector((state) => state?.driverPerformanceDashboardRedux?.filters?.globals);
    const mainTab = useSelector((state) => state?.driverPerformanceMainRedux?.tab);

    const getReturnTab = () => {
        const urlParams = new URLSearchParams(location.search);
        const tabParam = urlParams.get('tab');
        if (tabParam) return tabParam;
        switch (mainTab) {
            case 0:
                return 'dashboard';
            case 2:
                return 'events';
            case 3:
                return 'cases';
            case 1:
            default:
                return 'drivers';
        }
    };

    const getRangeForDriverDetail = () => {
        const urlParams = new URLSearchParams(location.search);

        const tabParam = urlParams.get('tab');
        const isCasesContext =
            tabParam === 'cases' ||
            mainTab === 3 ||
            location.pathname?.includes('/driverPerformance/cases') ||
            location.pathname?.includes('/driverPerformance/caseDetail');

        const isEventsContext = tabParam === 'events' || mainTab === 2;
        const isDriversContext = tabParam === 'drivers' || mainTab === 1;

        if (isCasesContext && casesLastSearchRange?.GTE && casesLastSearchRange?.LTE) {
            const hasValidRange =
                moment(casesLastSearchRange.GTE).isValid() &&
                moment(casesLastSearchRange.LTE).isValid();

            if (hasValidRange) {
                return {
                    startDate: moment.utc(casesLastSearchRange.GTE).startOf('day').toISOString(),
                    endDate: moment.utc(casesLastSearchRange.LTE).endOf('day').toISOString()
                };
            }
        }

        const startParam = isEventsContext
            ? urlParams.get('eventsStartDate')
            : (isDriversContext ? urlParams.get('driversStartDate') : null);
        const endParam = isEventsContext
            ? urlParams.get('eventsEndDate')
            : (isDriversContext ? urlParams.get('driversEndDate') : null);
        const rangeParam = isEventsContext
            ? urlParams.get('eventsRange')
            : (isDriversContext ? urlParams.get('driversRange') : null);

        const hasStartEndParams =
            Boolean(startParam && endParam) &&
            moment(startParam).isValid() &&
            moment(endParam).isValid();

        if (hasStartEndParams) {
            return {
                startDate: moment.utc(startParam).startOf('day').toISOString(),
                endDate: moment.utc(endParam).endOf('day').toISOString()
            };
        }

        if (rangeParam) {
            const range = getInitialRange(rangeParam);
            if (range?.start && range?.end) {
                return {
                    startDate: moment.utc(range.start).startOf('day').toISOString(),
                    endDate: moment.utc(range.end).endOf('day').toISOString()
                };
            }
        }

        if (dashboardGlobals?.timeFrame) {
            const isCustom = dashboardGlobals?.timeFrame === 'custom';

            if (!isCustom) {
                const range = getInitialRange(dashboardGlobals.timeFrame);
                if (range?.start && range?.end) {
                    return {
                        startDate: moment.utc(range.start).startOf('day').toISOString(),
                        endDate: moment.utc(range.end).endOf('day').toISOString()
                    };
                }
            }

            if (isCustom && dashboardGlobals?.rangeDate?.start && dashboardGlobals?.rangeDate?.end) {
                return {
                    startDate: moment.utc(dashboardGlobals.rangeDate.start).startOf('day').toISOString(),
                    endDate: moment.utc(dashboardGlobals.rangeDate.end).endOf('day').toISOString()
                };
            }
        }

        if (driverTabDateStart && driverTabDateEnd) {
            return {
                startDate: moment.utc(driverTabDateStart).startOf('day').toISOString(),
                endDate: moment.utc(driverTabDateEnd).endOf('day').toISOString()
            };
        }

        return {
            startDate: moment.utc().startOf('month').toISOString(),
            endDate: moment.utc().endOf('month').toISOString()
        };
    };

    const buildDriverDetailUrl = (driverIdToOpen) => {
        const { startDate, endDate } = getRangeForDriverDetail();
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        params.append('sourceModule', 'dashboardCaseInfo');
        params.append('returnTab', getReturnTab());
        return `/app/driverPerformance/driverDetail/${driverIdToOpen}?${params.toString()}`;
    };

    useEffect(() => {
        if (openTooltip) {
            dispatch(getCase({ caseNumber }))
        }
    }, [openTooltip])

    return (
        <div style={{ minHeight: 45, width: 255 }}>
            {
                loading ? <div><Loading full /></div > :
                    <>
                        {
                            caseInfo ? (
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 25 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_caseId']}:</strong>
                                        <a
                                            href='#'
                                            noWrap
                                            style={{
                                                color: '#005bf8',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                margin: 0,
                                                flex: 1,
                                                minWidth: 0
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(`/app/driverPerformance/caseDetail/${caseInfo?.id}`)
                                            }} >
                                            {caseInfo?.caseNumber}
                                        </a>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 20 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_status']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{messages[`pending_coach_case_status_${caseInfo?.status?.label.toLowerCase()}`] || caseInfo?.status?.label || 'N/A'}</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 20 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_next_step']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{messages[`pending_coach_case_next_step_${caseInfo?.status?.nextStep?.toString().trim().toLowerCase().replaceAll(" ", "_")}`] || caseInfo?.status?.nextStep || 'N/A'}</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 20 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_created']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{caseInfo?.createdOn ? <Timezone date={caseInfo?.createdOn} dateFormat={dateformat} timeFormat="hh:mm a" timezone={timeZone} /> : 'N/A'}</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 20 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_driver']}:</strong>
                                        <a
                                            href='#'
                                            noWrap
                                            style={{
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                margin: 0,
                                                flex: 1,
                                                minWidth: 0,
                                                color: '#005bf8',
                                                textDecoration: 'underline',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => navigate(buildDriverDetailUrl(caseInfo?.driver?.id))}
                                        >
                                            {caseInfo?.driver?.name}
                                        </a>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 20 }}>
                                        <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_coach']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{caseInfo?.coach?.firstName || 'N/A'}</p>
                                    </div>
                                    <Divider sx={{ mt: 1, mb: 1 }} />
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 20 }}>
                                            <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_created']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{caseInfo?.createdOn ? <Timezone
                                                date={caseInfo?.createdOn}
                                                dateFormat={dateformat}
                                                timeFormat="hh:mm a"
                                                timezone={timeZone}
                                            /> : <></>}</p>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 20 }}>
                                            <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_evaluation']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{caseInfo?.startEvaluationDate ? <Timezone
                                                date={caseInfo?.startEvaluationDate}
                                                dateFormat={dateformat}
                                                timeFormat="hh:mm a"
                                                timezone={timeZone}
                                            /> : <>N/A</>}</p>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 20 }}>
                                            <strong style={{ marginRight: 5 }}>{messages['driver_performance_events_info_complete']}:</strong><p style={{ wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0, flex: 1, minWidth: 0 }}>{caseInfo?.completedDate ? <Timezone
                                                date={caseInfo?.completedDate}
                                                dateFormat={dateformat}
                                                timeFormat="hh:mm a"
                                                timezone={timeZone}
                                            /> : <>N/A</>}</p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100 }}>{messages['driver_performance_events_info_no_case_info']}</div>
                            )
                        }
                    </>
            }
        </div>
    )
}; 